// firebaseListeners.js

import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { Network } from '@capacitor/network';
import { Timestamp, firestore, doc, onSnapshot } from '@/firebaseConfig.js';

import { Posthog } from '@/vendors/Posthog';
import { Sentry } from '@/vendors/Sentry';

import { GetVersion, SendError } from '@/helpers.js';

import router from '@/router/router';
import store from '@/store/store';
import { collection, orderBy, limit, query, where } from 'firebase/firestore';
import { supportedNotificationTypes } from './utils/notifications';

let userListener = null;
let notificationsObserver = null;
let chatsListener = null;

// Auth State Change
const authStateChange = async (user) => {
  // Initialize
  if (!user) {
    return;
  }

  // Set current user (MUST BE FIRST)
  store.commit('setCurrentUser', user);

  // Update or create user
  await syncWithNetwork();
};

async function syncWithNetwork() {
  try {
    store.commit('stats/resetState');
    await Promise.allSettled([
      store.dispatch('updateUserProfile', {
        accessedAt: Timestamp.now(),
      }),
      store.dispatch('getOrganizations'),
      store.dispatch('check'),
    ]);
    if (store.getters.currentOrganization?.payment) {
      await store.dispatch('getBillingDetails', store.getters.currentOrganization.id);
    }
    if (Capacitor.isNativePlatform()) {
      await store.dispatch('fcm/initPushNotifications');
      App.removeAllListeners();
      App.addListener('resume', () => {
        if (Capacitor.isNativePlatform()) {
          store.dispatch('fcm/removeAllDeliveredNotifications');
        }
      });
    }

    const user = store.state.currentUser;

    // Set Sentry User
    Sentry.setUser({
      ip_address: '{{auto}}',
      username: user.displayName,
      email: user.email,
      id: user.uid,
      release: GetVersion(),
    });

    // Set Posthog User
    await Posthog.identify({
      distinctId: user.uid,
      userProperties: {
        name: user.displayName,
        email: user.email,
        id: user.uid,
        release: GetVersion(),
        environment: import.meta.env.VITE_NODE_ENV,
      },
    });

    // ========================================
    // User Listener
    let isFirstUserProfileCall = true;
    let refreshedUnix = 0;
    userListener = onSnapshot(doc(firestore, 'users', user.uid), async (doc) => {
      let userProfile = doc.data();
      if (!userProfile || userProfile.status === 'deleted') {
        SendError('Your account has been deleted.');
        await store.dispatch('logout');
        await router.push('/');
        return;
      }
      if (userProfile) {
        userProfile.id = doc.id;
        store.commit('setUserProfile', userProfile);

        if (isFirstUserProfileCall) {
          isFirstUserProfileCall = false;
          if (!userProfile.audioURL) {
            router.push('/speech-sample');
          }

          refreshedUnix = userProfile.refreshAt?.seconds || 0;
          return;
        }

        // Check for data refresh
        if (userProfile.refreshAt?.seconds > refreshedUnix) {
          store.dispatch('stats/fetchTeamStats');
        }
      }
    });

    // ========================================
    // Notifications Listener
    let isFirstNotificationCall = true;
    notificationsObserver = onSnapshot(
      query(
        collection(firestore, 'users', user.uid, 'notifications'),
        orderBy('createdAt', 'desc'),
        limit(70)
      ),
      async (notificationsSnapshot) => {
        if (!isFirstNotificationCall) {
          if (membersCountChanged(notificationsSnapshot)) {
            await store.dispatch('getOrganizations');
            await store.dispatch('stats/fetchTeamStats');
          }
          const changedRecordings = getChangedRecordings(notificationsSnapshot);
          if (changedRecordings.length > 0) {
            // TODO: Check which one are changed and fetch only those
            await store.dispatch('stats/fetchTeamStats');
            changedRecordings.forEach((recordingId) => {
              store.dispatch('recs/fetchRecording', { recordingId });
            });
          }
          updateNewReactions(notificationsSnapshot);
        }
        isFirstNotificationCall = false;
        notificationsSnapshot.docChanges().forEach((change) => {
          const notificationData = change.doc.data();

          if (
            supportedNotificationTypes.includes(notificationData.type) &&
            store.state.userProfile.preferences.notifications[notificationData.type].enabled
          ) {
            if (change.type === 'removed') {
              store.commit('removeNotification', {
                id: change.doc.id,
              });
            } else {
              store.commit('addNotification', {
                id: change.doc.id,
                ...notificationData,
              });
            }
          }
        });
      }
    );

    // ========================================
    // Chats Listener
    let isFirstChatsCall = true;

    // TODO: Paginate?

    chatsListener = onSnapshot(
      query(
        collection(firestore, 'chats'),
        where('uid', '==', user.uid),
        orderBy('createdAt', 'desc'),
        limit(1000)
      ),
      async (chatsSnapshot) => {
        if (!isFirstChatsCall) {
        }
        isFirstChatsCall = false;
        let chats = store.state.chats.chats;

        chatsSnapshot.docChanges().forEach((change) => {
          const chatsData = change.doc.data();

          let chatId = change.doc.id;

          if (change.type === 'removed') {
            const index = chats.findIndex((chat) => chat.id === chatsData.id);
            if (index !== -1) {
              // Remove chat
              chats.splice(index, 1);
            }
          } else if (change.type === 'added' || change.type === 'modified') {
            const index = chats.findIndex((chat) => chat.id === chatsData.id);
            if (index === -1) {
              // Append new chat
              chats = [chatsData].concat(chats);
            } else {
              // Update existing chat
              chats[index] = chatsData;
            }
          }

          if (change.type === 'modified') {
            // Set message loading to false when chat is modified (not added)
            store.commit('chats/setMessageLoading', false);

            // Scroll to bottom
            if (store.state.chats.selectedChat?.id === chatId) {
              // Only Scroll if user is already at bottom
              const chatContainer = document.getElementById('chat-messages');
              const scrollArea = chatContainer.querySelector('.q-scrollarea__container');
              if (scrollArea.scrollTop + scrollArea.clientHeight >= scrollArea.scrollHeight - 100) {
                setTimeout(() => {
                  scrollArea.scrollTop = scrollArea.scrollHeight;
                }, 100);
              }
            }
          }

          store.commit('chats/setChats', chats);
        });
      }
    );
  } catch (err) {
    Sentry.captureException(err);
    await store.dispatch('logout');
    await router.push('/');
  }
}

const removeListeners = async () => {
  userListener?.();
  notificationsObserver?.();
  chatsListener?.();
};

function membersCountChanged(notificationsSnapshot) {
  return notificationsSnapshot.docChanges().some((change) => {
    if (change.type !== 'added') return false;

    const { type } = change.doc.data();
    if (type === 'new-member' || type === 'member-removed') {
      return true;
    }
    return false;
  });
}

function updateNewReactions(notificationsSnapshot) {
  const uniqueRecordingIds = notificationsSnapshot
    .docChanges()
    .filter((change) => {
      if (change.type !== 'added') return false;
      const { type } = change.doc.data();
      return type === 'new-reaction';
    })
    .map(({ doc }) => doc.data().data.recordingId)
    .filter((recordingId, index, arr) => arr.indexOf(recordingId) === index);
  for (const recordingId of uniqueRecordingIds) {
    store.dispatch('recs/reactions/fetch', { recordingId });
  }
}

function getChangedRecordings(notificationsSnapshot) {
  const changedRecordings = notificationsSnapshot.docChanges().filter((change) => {
    if (change.type !== 'added') return false;

    const { type } = change.doc.data();
    if (type === 'recording-added' || type === 'recording-removed' || type === 'recording-failed') {
      return true;
    }
    return false;
  });
  return changedRecordings.map((change) => change.doc.data().data.recordingId);
}

Network.addListener('networkStatusChange', ({ connected }) => {
  const user = store.state.currentUser;
  if (connected && user) {
    syncWithNetwork();
  }
});

export { removeListeners, authStateChange };
